package fyi.moko.hotpl.data.service

import fyi.moko.hotpl.data.network.NetworkPlaceDataSource
import fyi.moko.hotpl.data.network.toDomain
import fyi.moko.hotpl.data.network.toPublicDto
import fyi.moko.hotpl.data.network.toRemoteDto
import fyi.moko.hotpl.domain.model.Place
import fyi.moko.hotpl.domain.model.SyncState
import fyi.moko.hotpl.domain.model.SyncAction
import fyi.moko.hotpl.domain.model.Visibility
import fyi.moko.hotpl.domain.repository.PlaceRepository
import fyi.moko.hotpl.domain.repository.YellowPageRepository
import fyi.moko.hotpl.data.image.PhotoSyncStore
import fyi.moko.hotpl.domain.model.SYNC_ENTITY_PHOTO
import fyi.moko.hotpl.domain.model.sharedPhotoFileNames
import fyi.moko.hotpl.domain.model.SyncQueueItem
import fyi.moko.hotpl.domain.service.AuthService
import fyi.moko.hotpl.domain.service.PhotoBlobStore
import fyi.moko.hotpl.domain.service.PhotoSyncQueue
import fyi.moko.hotpl.domain.service.ConnectivityObserver
import fyi.moko.hotpl.domain.service.SyncGate
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class SyncCoordinator(
    private val authService: AuthService,
    private val placeRepository: PlaceRepository,
    private val networkPlaceDataSource: NetworkPlaceDataSource,
    private val connectivityObserver: ConnectivityObserver,
    private val yellowPageRepository: YellowPageRepository,
    private val syncGate: SyncGate = SyncGate(),
    /**
     * Where photo bytes go. Null (tests, or a platform without Storage wired) leaves PHOTO
     * queue rows untouched rather than draining them as if they had been uploaded.
     */
    private val photoBlobStore: PhotoBlobStore? = null,
    /** Local files to read for upload; [PhotoSyncStore.loadLocal] so an upload never triggers a fetch. */
    private val photoFiles: PhotoSyncStore? = null,
    /** Where [backfillPhotoUploads] queues the photos that predate photo sync. */
    private val photoQueue: PhotoSyncQueue? = null,
) {
    private var currentBackoffMs = DEFAULT_POLL_INTERVAL_MS

    /**
     * Held while stranded places are being adopted. Unlike [syncGate] this only holds *push* back —
     * the adoption runs inside the pull collector, so pausing the gate from there would cancel the
     * very coroutine doing the work.
     */
    private val adopting = MutableStateFlow(false)

    /**
     * Becomes true after the first remote snapshot for places is received (the initial
     * pull completes). Drives the "this account has no saved places" hint so the user can
     * distinguish an empty remote dataset from a sync that hasn't happened yet (backlog 0-9).
     */
    private val _firstPullCompleted = MutableStateFlow(false)
    val firstPullCompleted: StateFlow<Boolean> = _firstPullCompleted.asStateFlow()

    /** Regionkey most recently requested via [requestRegionSync]; null until first requested. */
    private val _pendingRegionSync = MutableStateFlow<String?>(null)

    private val _isSyncingRegion = MutableStateFlow(false)
    val isSyncingRegion: StateFlow<Boolean> = _isSyncingRegion.asStateFlow()

    /** Bumped after a directory region sync completes successfully (see [requestRegionSync]). */
    private val _regionSyncTick = MutableStateFlow(0)
    val regionSyncTick: StateFlow<Int> = _regionSyncTick.asStateFlow()

    companion object {
        private const val CHUNK_SIZE = 20
        private const val DEFAULT_POLL_INTERVAL_MS = 5000L
        private const val MAX_BACKOFF_MS = 60000L
        private const val CHUNK_DELAY_MS = 500L

        /**
         * Sentinel "file" in the image store recording that [backfillPhotoUploads] ran.
         * Versioned: bumping the suffix is how a device is made to sweep once more.
         */
        internal const val PHOTO_BACKFILL_MARKER = ".photo-backfill-v2"
    }

    /**
     * Requests a Yellow Page directory region sync for [regionKey] (F-11). Routed through this
     * coordinator instead of [DirectoryViewModel][fyi.moko.hotpl.ui.directory.DirectoryViewModel]
     * calling [YellowPageRepository.syncRegion] directly, so it gets the same connectivity gate
     * and retry/backoff as every other sync path in the app rather than a single unmanaged
     * attempt that silently gives up offline. A newer request supersedes an older one still
     * retrying (see the worker started in [startSync]).
     */
    fun requestRegionSync(regionKey: String) {
        _pendingRegionSync.value = regionKey
    }

    fun startSync(scope: CoroutineScope) {
        scope.launch {
            try {
                // 1. Ensure anonymous sign-in before any sync activity
                authService.ensureSignedIn()
            } catch (e: Exception) {
                // No retry here: this bootstrap runs once per process. Until a user exists,
                // sync stays idle (the combine below never gets a uid) and the Account screen
                // recovers on its own — LinkAccountUseCase signs in directly when there is no
                // current user, and signOut re-mints an anonymous session itself.
                println("HotPl sync: anonymous sign-in failed at launch: ${e.message}")
            }

            // 2. Observe authentication state to start pull sync listeners. A paused gate reads as
            //    "no uid": collectLatest then cancels the running pull, detaching the snapshot
            //    listener for the duration of an account switch, and re-attaches on resume with
            //    whatever uid the switch settled on.
            //
            //    Connectivity is part of the key so that coming back online re-runs this block —
            //    that is what gives the adoption below a retry after an offline start.
            combine(
                authService.currentUserId,
                syncGate.isPaused,
                connectivityObserver.isOnline,
            ) { uid, paused, online -> uid.takeUnless { paused }?.takeIf { online } }
                .distinctUntilChanged()
                .collectLatest { uid ->
                    // A new pull epoch (account switch, sign-out, connectivity/pause change)
                    // invalidates the previous account's "first pull done" flag — the hint
                    // must not fire for account B based on account A's completed pull.
                    _firstPullCompleted.value = false
                    if (uid != null) {
                        placeRepository.brandLocalPlacesWithUser(uid)
                        placeRepository.brandLocalVisitsWithUser(uid)
                        backfillPhotoUploads()
                        // Must precede pullSync: an unadopted place can be overwritten by the very
                        // first snapshot if it shares a (legacy) id with one this account owns.
                        adoptStrandedPlaces(uid)
                        pullSync(uid)
                    }
                }
        }

        // 3. Periodic push sync worker (runs in background)
        scope.launch {
            combine(
                connectivityObserver.isOnline,
                syncGate.isPaused,
                adopting,
            ) { online, paused, adopting -> online && !paused && !adopting }
                .distinctUntilChanged()
                .collectLatest { canPush ->
                    if (canPush) {
                        currentBackoffMs = DEFAULT_POLL_INTERVAL_MS
                        while (true) {
                            var success = false
                            try {
                                val hasMore = pushPendingChanges()
                                success = true
                                if (hasMore) {
                                    delay(CHUNK_DELAY_MS)
                                    continue
                                }
                            } catch (e: CancellationException) {
                                // Must propagate, not be swallowed as a "network error" below —
                                // otherwise this loop survives its own scope's cancellation and
                                // keeps rescheduling delay() forever (caught this hanging a test).
                                throw e
                            } catch (e: Exception) {
                                println("SyncCoordinator: push cycle failed: ${e.message ?: e::class.simpleName}")
                            }

                            if (success) {
                                currentBackoffMs = DEFAULT_POLL_INTERVAL_MS
                            } else {
                                currentBackoffMs = (currentBackoffMs * 2).coerceAtMost(MAX_BACKOFF_MS)
                            }
                            delay(currentBackoffMs)
                        }
                    }
                }
        }

        // 4. Directory region sync worker (F-11): waits for connectivity like push, and retries
        // with the same backoff on failure instead of the one-shot attempt DirectoryViewModel
        // used to make directly. collectLatest means a newer requestRegionSync call (or losing
        // connectivity) cancels an in-flight retry loop for the previous region.
        scope.launch {
            combine(_pendingRegionSync, connectivityObserver.isOnline) { regionKey, online -> regionKey to online }
                .collectLatest { (regionKey, online) ->
                    if (regionKey == null || !online) return@collectLatest
                    _isSyncingRegion.value = true
                    try {
                        var backoffMs = DEFAULT_POLL_INTERVAL_MS
                        while (true) {
                            val result = yellowPageRepository.syncRegion(regionKey)
                            if (result.isSuccess) {
                                // Re-run the current search: the user may have typed before the
                                // cache finished populating.
                                _regionSyncTick.update { it + 1 }
                                break
                            }
                            println(
                                "SyncCoordinator: yellow page region sync failed: " +
                                    "${result.exceptionOrNull()?.message ?: "unknown error"}",
                            )
                            delay(backoffMs)
                            backoffMs = (backoffMs * 2).coerceAtMost(MAX_BACKOFF_MS)
                        }
                    } finally {
                        _isSyncingRegion.value = false
                    }
                }
        }
    }

    /**
     * Pushes pending changes from the local queue in chunks.
     * Returns true if there might be more pending items remaining in the queue.
     *
     * Items whose place is not yet branded or is owned by a different uid than the current
     * authenticated user are skipped without touching the queue or the place's sync state. This
     * prevents writing a place under the wrong Firestore path or with the wrong ownerId. A blank
     * ownerId can happen when a place was saved while offline and [brandLocalPlacesWithUser] has
     * not finished yet; it will be branded and retried in the next cycle. A mismatched ownerId
     * happens when an account switch signed into a new uid but could not finish adopting the local
     * places (see [AccountSwitchIncompleteException]); the next successful [adoptStrandedPlaces] run
     * will rebrand them and push normally.
     */
    suspend fun pushPendingChanges(): Boolean {
        val uid = authService.getCurrentUserId() ?: return false
        val pendingItems = placeRepository.getPendingSyncItems(limit = CHUNK_SIZE)

        var skippedDueToOwnerMismatch = 0
        var failureCount = 0

        for (item in pendingItems) {
            // Photo rows share the queue but not the place logic below — a file name is not
            // a place id, and falling through would drop the row as "place not found".
            if (item.entityType == SYNC_ENTITY_PHOTO) {
                if (!pushPhoto(uid, item)) failureCount++
                kotlinx.coroutines.yield()
                continue
            }

            val placeId = item.entityId
            val place = placeRepository.getPlaceIncludingDeleted(placeId)

            // A place whose ownerId does not match the current auth uid must not be pushed under
            // that uid. This includes blank ownerId (not yet branded) as well as a stale ownerId
            // from a previous account. Leave the queue item and sync state untouched so the next
            // successful branding/adoption run can push normally.
            if (place != null && place.ownerId != uid) {
                skippedDueToOwnerMismatch++
                continue
            }

            // A queue item whose place is already SYNCED holds nothing left to push: the pull
            // loop merged an equal-or-newer remote copy over the local row (savePlaceFromRemote),
            // or a previous cycle crashed between markPlaceSynced and deleteSyncItem. Re-pushing
            // would send a version the server already has, which the deployed rules deny (updates
            // require a strictly increasing version) — permanently, on every retry. Drop the item.
            if (place != null && place.syncState == SyncState.SYNCED) {
                placeRepository.deleteSyncItem(item.queueId)
                continue
            }

            try {
                if (item.action == SyncAction.UPSERT) {
                    if (place != null) {
                        if (place.isDeleted) {
                            retireProjection(uid, item.entityId)
                            pushTombstoneIfRemoteDocExists(uid, item.entityId, place)
                        } else {
                            // PII-free pre-write log: no place id, no uid, no email — only
                            // whether the auth uid matches the ownerId, the version, and whether
                            // the timestamps look sane. This is what pinpoints a stale-version
                            // create rejection (the prod rules require version == 1 on create)
                            // vs an owner mismatch vs a clock skew.
                            val ownerMatch = place.ownerId == uid
                            val op = "upsert"
                            println(
                                "HotPl push-pre: op=$op ownerMatch=$ownerMatch version=${place.version} " +
                                    "createdAt=${place.createdAt} updatedAt=${place.updatedAt} " +
                                    "createdAtPresent=${place.createdAt > 0} updatedAtPresent=${place.updatedAt > 0} " +
                                    "updatedAfterCreated=${place.updatedAt >= place.createdAt} " +
                                    "status=${place.status.name} visibility=${place.visibility.name}"
                            )
                            // Projection first, place document second. The projection write
                            // is idempotent (no version rule) but the place write is not: once
                            // users/{uid}/places/{id} lands at version N, a retry of the same
                            // version is denied forever. If the projection step (a get, a
                            // Storage upload, a set) fails *after* the place write, the queue
                            // item can never drain — exactly what jammed King Taco on
                            // 2026-09-18. In this order a failure leaves nothing half-done.
                            // PUBLIC and FRIENDS both live in /public_places; the Firestore
                            // read rules (not the write path) decide who sees a FRIENDS doc.
                            if (place.visibility != Visibility.PRIVATE) {
                                publishProjection(uid, place)
                            } else {
                                retireProjection(uid, item.entityId)
                            }
                            networkPlaceDataSource.savePlace(uid, place.toRemoteDto())
                        }
                        placeRepository.markPlaceSynced(placeId, SyncState.SYNCED)
                    }
                } else if (item.action == SyncAction.DELETE) {
                    // Same ordering argument as the upsert: retire first, tombstone second.
                    retireProjection(uid, item.entityId)
                    if (place != null) {
                        pushTombstoneIfRemoteDocExists(uid, item.entityId, place)
                    } else {
                        networkPlaceDataSource.deletePlace(uid, item.entityId, null)
                    }
                }
                placeRepository.deleteSyncItem(item.queueId)
            } catch (e: Exception) {
                // One item failing (e.g. a payload the backend permanently rejects) must not block
                // the rest of the chunk from pushing. Leave its queue entry and sync state untouched
                // so it retries next cycle, and keep going with the remaining items.
                val ownerMatch = place?.ownerId == uid
                val version = place?.version
                println(
                    "HotPl push-fail: op=${item.action.name} ownerMatch=$ownerMatch version=$version " +
                        "authPresent=${uid.isNotBlank()} error=${e::class.simpleName ?: "unknown"} " +
                        "message=${e.diagnosticMessage()}"
                )

                // Stale-version recovery: a place that was rebranded before the version-reset
                // fix (3528c35) carries version > 1 into a brand-new Firestore doc path, which
                // the prod rules reject on create (version == 1 required). If the doc doesn't
                // exist remotely yet, this is a create — reset the version to 1 and retry once.
                // (If the doc *does* exist, the version must only increase, so we leave it.)
                if (place != null && !place.isDeleted && place.version > 1L && item.action == SyncAction.UPSERT) {
                    try {
                        val exists = networkPlaceDataSource.placeExists(uid, item.entityId)
                        println("HotPl push-fail: stale-version recovery — remoteExists=$exists version=${place.version}")
                        if (!exists) {
                            placeRepository.resetPlaceForRemoteCreate(item.entityId)
                            // Retry the push with the reset version.
                            val fixed = placeRepository.getPlaceIncludingDeleted(item.entityId)
                            if (fixed != null && !fixed.isDeleted) {
                                if (fixed.visibility != Visibility.PRIVATE) {
                                    publishProjection(uid, fixed)
                                } else {
                                    retireProjection(uid, item.entityId)
                                }
                                networkPlaceDataSource.savePlace(uid, fixed.toRemoteDto())
                                placeRepository.markPlaceSynced(placeId, SyncState.SYNCED)
                                placeRepository.deleteSyncItem(item.queueId)
                                println("HotPl push-fail: stale-version recovery succeeded")
                                // Don't count this as a failure — it recovered.
                            }
                        }
                    } catch (recovery: Exception) {
                        println("HotPl push-fail: stale-version recovery failed: ${recovery::class.simpleName}")
                        failureCount++
                    }
                } else {
                    failureCount++
                }
            }
            kotlinx.coroutines.yield()
        }

        // Diagnostic log without leaking uid or place id. Only counts are exposed.
        if (skippedDueToOwnerMismatch > 0) {
            println("SyncCoordinator: skipped $skippedDueToOwnerMismatch pending item(s) awaiting ownership adoption")
        }
        if (failureCount > 0) {
            println("SyncCoordinator: failed to push $failureCount of ${pendingItems.size} pending item(s)")
            // Surfacing this after processing the whole chunk still triggers the caller's backoff,
            // without having skipped any item that could otherwise have been pushed this cycle.
            throw SyncPushPartialFailureException(failureCount, pendingItems.size)
        }

        // Stage 2: push pending visits (small batch after places). Must run even when the place
        // queue is empty — a visit added/deleted on an already-synced place enqueues nothing on
        // the place side, so returning early above would leave it PENDING_UPSERT forever.
        val visitsHaveMore = pushPendingVisits(uid)

        return pendingItems.size >= CHUNK_SIZE || visitsHaveMore
    }

    /**
     * Queues an upload for every photo this device already holds, once per
     * [PHOTO_BACKFILL_MARKER] version.
     *
     * Photos saved before photo sync existed never went through [CloudBackedPlaceImageStore]
     * and so never enqueued anything; without this they would stay on the device that took
     * them forever. Re-uploading a file that is already remote is harmless (same bytes, same
     * key), so the marker only exists to avoid doing this work on every launch. The marker is
     * a file in the image store so a restore (`clear()`) naturally re-runs the backfill.
     *
     * ── 마커에 버전을 붙인 이유 (백로그 0-18) ──────────────────────────────
     * 원래는 "기기당 한 번"이었다. 그런데 이 스윕은 업로드가 유실된 사진을 되살리는
     * 유일한 경로이기도 하다 — [pushPhoto]가 로컬 파일 없는 행을 큐가 막히지 않게
     * 버리고 나면, 문서는 그 사진 이름을 계속 참조하는데 다시 올려 줄 계기가 없다.
     * 마커가 이미 찍힌 뒤에 그런 일이 생기면 영영 회수되지 않는다(2026-09-08, 사진
     * 4장 실제 유실). 그래서 접미사를 올려 한 번 더 돌린다.
     *
     * 재발이 계속 관측되면 그때는 버전업이 아니라 원격 객체와 대조하는 스윕이 답이다
     * (`PhotoBlobStore.exists`가 필요해 actual 3개가 늘어난다). 지금은 그 값을 치르지
     * 않는다 — 설계 근거는 `docs/photo-recovery-0-18-design.md`.
     */
    internal suspend fun backfillPhotoUploads() {
        val files = photoFiles ?: return
        val queue = photoQueue ?: return
        if (files.loadLocal(PHOTO_BACKFILL_MARKER) != null) return
        var queued = 0
        for (name in placeRepository.photoFileNamesInUse()) {
            if (files.loadLocal(name) == null) continue
            queue.enqueueUpload(name)
            queued++
        }
        files.saveAs(PHOTO_BACKFILL_MARKER, byteArrayOf(1))
        println("HotPl photo-backfill: queued $queued upload(s)")
    }

    /**
     * Uploads or removes one photo per its queue row. Returns false on a failure that should
     * retry (the row is left in place); true when the row was drained.
     *
     * A local file that no longer exists at upload time (deleted before the push ran, or a
     * store that couldn't persist it) drains the row: there is nothing to send, and leaving it
     * would jam the queue forever. Rows are skipped, not drained, when no blob store is wired.
     *
     * ── 드롭은 조용히 하지 않는다 (0-18 A-2) ──────────────────────────────
     * 그 파일을 어떤 장소 문서가 아직 참조하고 있으면 바이트가 진짜로 사라진 것이다 —
     * [PhotoSyncStore.markLost]로 남겨 화면이 "이 기기에서 사라졌다"고 말하게 한다. 문서가
     * 이미 놓은 이름(사용자가 push 전에 지운 사진, R-6)은 잃은 것이 없으니 표식도 없다.
     * 2026-09-08에 4장이 이 경로로 흔적 없이 사라졌고, 릴리스 빌드는 아래 println을 남기지
     * 않는다. 표식이 유일한 흔적이다.
     */
    private suspend fun pushPhoto(uid: String, item: SyncQueueItem): Boolean {
        val blobs = photoBlobStore ?: return true
        val fileName = item.entityId
        try {
            when (item.action) {
                SyncAction.UPSERT -> {
                    val bytes = photoFiles?.loadLocal(fileName)
                    if (bytes == null) {
                        println("HotPl photo-push: local file missing at upload time; dropping row")
                        if (fileName in placeRepository.photoFileNamesInUse()) photoFiles?.markLost(fileName)
                    } else {
                        blobs.upload(uid, fileName, bytes)
                    }
                }
                SyncAction.DELETE -> blobs.delete(uid, fileName)
            }
            placeRepository.deleteSyncItem(item.queueId)
            return true
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            println(
                "HotPl photo-push: op=${item.action.name} failed " +
                    "error=${e::class.simpleName ?: "unknown"} message=${e.diagnosticMessage()}"
            )
            return false
        }
    }

    /**
     * Writes the FRIENDS/PUBLIC projection for [place] and brings the friend-visible photo
     * copies under `shared/{uid}/` in line with it (backlog 0-36).
     *
     * The wanted set is [sharedPhotoFileNames]; the previous set is whatever the remote
     * projection listed. Only the difference moves: a name already shared is not uploaded
     * again, a name no longer wanted is deleted. Bytes come from [PhotoSyncStore.load], so a
     * cover taken on another device is fetched from the account's own store first. A cover
     * whose bytes are nowhere is simply left out of the projection — the place still pushes,
     * and the next cover change or push retries it.
     *
     * Without a [photoBlobStore] the projection carries no photos at all rather than names a
     * friend could never fetch.
     */
    private suspend fun publishProjection(uid: String, place: Place) {
        val blobs = photoBlobStore
        // Food photos live on the visits (slice B); the flow's first value is the live list.
        val visits = placeRepository.observeVisits(place.id).first()
        val dto = place.toPublicDto(visits)
        if (blobs == null) {
            networkPlaceDataSource.savePublicProjection(uid, dto.copy(photos = emptyList()))
            return
        }
        val previous = networkPlaceDataSource.getPublicProjection(uid, place.id)?.photos.orEmpty()
        val shared = mutableListOf<String>()
        for (fileName in dto.photos) {
            if (fileName in previous) {
                shared += fileName
                continue
            }
            val bytes = photoFiles?.load(fileName)
            if (bytes == null) {
                println("HotPl photo-share: cover bytes unavailable; projection goes without it")
                continue
            }
            blobs.uploadShared(uid, fileName, bytes)
            shared += fileName
        }
        networkPlaceDataSource.savePublicProjection(uid, dto.copy(photos = shared))
        for (stale in previous - shared.toSet()) blobs.deleteShared(uid, stale)
    }

    /** Removes the projection and every shared photo copy it listed. */
    private suspend fun retireProjection(uid: String, placeId: String) {
        val blobs = photoBlobStore
        val previous = if (blobs == null) emptyList() else
            networkPlaceDataSource.getPublicProjection(uid, placeId)?.photos.orEmpty()
        networkPlaceDataSource.deletePublicProjection(uid, placeId)
        for (fileName in previous) blobs?.deleteShared(uid, fileName)
    }

    /**
     * Writes the soft-delete tombstone for [place] only when its remote doc actually exists.
     * A tombstone `set()` on a doc that never reached the remote (created and deleted before the
     * first successful push) is a CREATE to Firestore, and the deployed rules only allow creates
     * with `isDeleted == false && version == 1` — the write would be denied on every retry,
     * jamming the queue forever. Nothing exists remotely, so there is nothing to tombstone; the
     * caller still drains the queue item.
     */
    private suspend fun pushTombstoneIfRemoteDocExists(uid: String, placeId: String, place: Place) {
        if (networkPlaceDataSource.placeExists(uid, placeId)) {
            networkPlaceDataSource.deletePlace(uid, placeId, place.toRemoteDto())
        }
    }

    private suspend fun pushPendingVisits(uid: String): Boolean {
        val pendingVisits = placeRepository.getPendingVisitSyncItems(CHUNK_SIZE)
        var failureCount = 0
        for (visit in pendingVisits) {
            if (visit.ownerId != uid) continue
            try {
                if (visit.isDeleted) {
                    networkPlaceDataSource.deleteVisit(uid, visit.placeId, visit.id)
                } else {
                    networkPlaceDataSource.saveVisit(uid, visit.placeId, visit.toRemoteDto())
                }
                // A visit-only change never touches the place queue, but it can change what
                // the projection shares (food photos, slice B). Republish before marking the
                // visit synced: if this fails the visit stays pending and the next cycle
                // retries both (saveVisit is an idempotent set).
                val place = placeRepository.getPlace(visit.placeId)
                if (place != null && place.visibility != Visibility.PRIVATE && place.ownerId == uid) {
                    publishProjection(uid, place)
                }
                placeRepository.markVisitSynced(visit.id, SyncState.SYNCED)
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) {
                // Leave for next cycle — partial failure isolation like places.
                failureCount++
            }
            kotlinx.coroutines.yield()
        }

        if (failureCount > 0) {
            println("SyncCoordinator: failed to push $failureCount of ${pendingVisits.size} pending visit(s)")
            // Same reasoning as the place chunk: surfaced only after every item in the chunk has
            // been attempted, purely so the caller's backoff engages instead of retrying immediately.
            throw SyncPushPartialFailureException(failureCount, pendingVisits.size)
        }

        return pendingVisits.size >= CHUNK_SIZE
    }

    /**
     * Re-owns places left behind by an account switch that signed in but could not carry them over
     * (see [fyi.moko.hotpl.domain.usecase.AccountSwitchIncompleteException]).
     *
     * Being stranded is not a safe resting state. Push now filters by owner, but a stranded place
     * would still be stuck in the queue indefinitely if no one re-owns it; and if its (pre-v9,
     * numeric) id happens to match one this account already owns, the next pull overwrites it with
     * an unrelated place. Adopting them re-owns them onto [uid] and remaps those id collisions, the
     * same way the switch itself would have.
     *
     * The remote id list is required for that remap, so if it can't be read we leave the places
     * alone and try again — coming back online re-runs the caller.
     */
    private suspend fun adoptStrandedPlaces(uid: String) {
        val strandedOwners = placeRepository.strandedOwnerIds(uid)
        if (strandedOwners.isEmpty()) return

        adopting.value = true
        try {
            val reservedIds = networkPlaceDataSource.placeIdsFor(uid)
            // Best-effort, matching LinkAccountUseCase.finishSwitch: unlike reservedIds (whose
            // absence risks silently overwriting a colliding place), a failure here just means
            // dedup doesn't happen and adoption proceeds with a possible visible duplicate
            // rather than skipping the whole adoption.
            val duplicateKeys = try {
                networkPlaceDataSource.duplicateKeysFor(uid)
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) {
                println("HotPl adopt: duplicateKeysFor failed, proceeding without dedup: ${e::class.simpleName}")
                emptyMap()
            }
            for (previousUid in strandedOwners) {
                placeRepository.rebrandPlaces(
                    fromUid = previousUid,
                    toUid = uid,
                    reservedIds = reservedIds,
                    duplicateKeys = duplicateKeys,
                )
            }
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            // Offline or the read failed. Rebranding without the taken ids could let a colliding
            // place be overwritten, which is the very thing this repairs — so do nothing and retry.
        } finally {
            adopting.value = false
        }
    }

    /**
     * Collects the remote snapshot stream for [uid] until cancelled. Deliberately a suspend
     * collect rather than a `launch` into an outer scope: the caller's `collectLatest` can only
     * detach the Firestore listener — on an account switch, or when [syncGate] pauses — if this
     * runs *inside* the lambda it cancels. Launching it into the enclosing scope instead left the
     * old uid's listener alive, so a switch ended up with two listeners writing to the same DB.
     */
    private suspend fun pullSync(uid: String) = coroutineScope {
        // Firestore snapshot listeners never complete, so a visit listener launched per place
        // must run as a sibling child job, not be `collect`-ed inline — collecting it here would
        // block this for-loop (and the outer observePlaces collector) on the first place forever.
        // Jobs are keyed by placeId and cancelled once that place drops out of the snapshot (e.g.
        // remote delete), and all of them are cancelled for free when this coroutineScope exits
        // (account switch / syncGate pause cancels the caller's collectLatest branch).
        val visitJobs = mutableMapOf<String, Job>()
        try {
            networkPlaceDataSource.observePlaces(uid).collect { remotePlaces ->
                // Mark the first pull as completed so the UI can distinguish "sync hasn't
                // happened" from "this account has zero places" (backlog 0-9).
                if (!_firstPullCompleted.value) {
                    _firstPullCompleted.value = true
                }
                val activePlaceIds = mutableSetOf<String>()
                for (remoteDto in remotePlaces) {
                    val remotePlace = remoteDto.toDomain()
                    val local = placeRepository.getPlaceIncludingDeleted(remotePlace.id)

                    if (local == null) {
                        if (!remotePlace.isDeleted) {
                            placeRepository.savePlaceFromRemote(remotePlace)
                        }
                    } else {
                        if (remotePlace.version > local.version ||
                            (remotePlace.version == local.version && remotePlace.updatedAt > local.updatedAt)) {
                            placeRepository.savePlaceFromRemote(remotePlace)
                        }
                    }

                    // Pull visits for this place (non-deleted places only), one listener per place.
                    if (!remotePlace.isDeleted) {
                        activePlaceIds += remotePlace.id
                        if (remotePlace.id !in visitJobs) {
                            visitJobs[remotePlace.id] = launch {
                                try {
                                    networkPlaceDataSource.observeVisits(uid, remotePlace.id).collect { remoteVisits ->
                                        for (visitDto in remoteVisits) {
                                            val remoteVisit = visitDto.toDomain()
                                            val localVisit = placeRepository.getVisitIncludingDeleted(remoteVisit.id)

                                            // Same LWW guard as places: a local pending edit/delete must not
                                            // be clobbered by a stale (or resurrected) remote snapshot.
                                            if (localVisit == null) {
                                                if (!remoteVisit.isDeleted) {
                                                    placeRepository.saveVisitFromRemote(remoteVisit)
                                                }
                                            } else if (remoteVisit.version > localVisit.version ||
                                                (remoteVisit.version == localVisit.version && remoteVisit.updatedAt > localVisit.updatedAt)) {
                                                placeRepository.saveVisitFromRemote(remoteVisit)
                                            }
                                        }
                                    }
                                } catch (e: CancellationException) {
                                    // Must propagate — swallowing it here would leave this child job's
                                    // parent (the outer coroutineScope / job cancel below) unaware it
                                    // finished, same hazard as the push worker's loop.
                                    throw e
                                } catch (e: Exception) {
                                    // Visit subcollection may not exist yet; tolerated, but logged
                                    // for the same reason as the place stream catch below.
                                    println("SyncCoordinator: visit stream for a place failed: ${e.message ?: e::class.simpleName}")
                                }
                            }
                        }
                    }
                }

                // Stop listening to places that dropped out of the snapshot (deleted/removed).
                val stalePlaceIds = visitJobs.keys - activePlaceIds
                for (placeId in stalePlaceIds) {
                    visitJobs.remove(placeId)?.cancel()
                }
            }
        } catch (e: CancellationException) {
            // Must propagate, not be swallowed as a stream failure below — otherwise an account
            // switch or syncGate pause cancelling this coroutineScope leaves the cancellation
            // unobserved, mirroring the push worker's while(true) hang this same fix addressed there.
            throw e
        } catch (e: Exception) {
            // Swallowed on purpose (stream failure ends this pull attempt; connectivity/uid
            // changes re-trigger it), but never silently: an invisible dead listener cost a
            // full debugging session on web (2026-07-13).
            println("SyncCoordinator: pull stream for places failed: ${e.message ?: e::class.simpleName}")
        } finally {
            visitJobs.values.forEach { it.cancel() }
        }
    }
}

/**
 * Thrown by [SyncCoordinator.pushPendingChanges] when one or more items in the chunk could not be
 * pushed (e.g. the backend permanently rejects a corrupted payload). The failing items are left in
 * the queue for the next cycle; this is thrown only after every other item in the chunk has been
 * attempted, purely so the caller's backoff still engages instead of retrying immediately.
 */
class SyncPushPartialFailureException(
    val failureCount: Int,
    val chunkSize: Int,
) : Exception("Failed to push $failureCount of $chunkSize pending item(s)")

/** Longest message a single failure may put in the log. */
internal const val MAX_DIAGNOSTIC_MESSAGE = 300

/**
 * Credential-shaped query parameters. The value is replaced; the key stays so the log still
 * shows what kind of link failed.
 */
private val SECRET_QUERY_PARAM =
    Regex("(token|key|signature|sig|access_token|auth)=[^&\\s]+", RegexOption.IGNORE_CASE)

/**
 * This throwable's message, redacted and capped, for a one-line failure log.
 *
 * ── 왜 메시지를 그냥 찍지 않는가 ──────────────────────────────
 *  · 사진 다운로드는 `getDownloadUrl()`이 준 **토큰이 박힌 URL**로 나간다. 전송 계층 예외의
 *    메시지에는 그 URL이 통째로 실릴 수 있고, 그 토큰 하나면 누구든 그 사진을 받아 간다.
 *    그래서 값만 지우고 키는 남긴다 — 어떤 종류의 링크가 실패했는지는 여전히 보여야 한다.
 *  · 그렇다고 클래스 이름만 찍으면 릴리스에서 쓸모가 없다. R8이 이름을 뭉개서
 *    `error=s` 한 글자로 나오는 것을 2026-09-07 알파 검증에서 실측했다.
 *
 * 사진 **다운로드** 실패 로그([CloudBackedPlaceImageStore])는 여전히 메시지를 찍지 않는다.
 * 그쪽은 토큰이 실릴 자리가 확실한 데다, 상태 코드만으로 원인이 갈린다.
 */
internal fun Throwable.diagnosticMessage(): String {
    val raw = message?.trim().orEmpty()
    if (raw.isEmpty()) return "none"
    return raw.replace(SECRET_QUERY_PARAM, "$1=***").take(MAX_DIAGNOSTIC_MESSAGE)
}
